import { ConversationMessageDateDivider } from '@modules/conversation-message/ConversationMessageDateDivider';
import { ConversationNewMessagesDivider } from '@modules/conversation-message/ConversationNewMessagesDivider';
import { ConversationVirtuoso } from '@modules/conversation/ConversationVirtuoso';
import type { Editor } from '@tiptap/react';
import { useEffect, useRef } from 'react';
import { LogLevel } from 'react-virtuoso';
import {
  ChatMessageListItem,
  IChatConversation,
  IChatMessage,
} from 'src/@types/chat';
import { Loading } from 'src/components/Loading/Loading';
import { markConversationAsRead } from 'src/redux/slices/chat';
import { dispatch, useSelector } from 'src/redux/store';
import { ChatInitialState } from 'src/sections/@dashboard/chat/ChatConversation/ChatInitialState/ChatInitialState';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { useMessages } from 'src/sections/@dashboard/chat/ChatConversation/useChatMessages';
import { useUpdateMessage } from 'src/sections/@dashboard/chat/ChatConversation/useUpdateMessage';
import { ChatMessageItem } from './ChatMessageItem';
// @ts-ignore
globalThis.VIRTUOSO_LOG_LEVEL = LogLevel.ERROR;

type Props = {
  conversation: IChatConversation;
  editor: Editor;
};

export function ChatMessageList({ conversation, editor }: Props) {
  const markedAsReadRef = useRef(false);

  const { goToMessage, setGoToMessage, setLoadGoTo, scrollEnd, editorConfig } =
    useChatConversationContext();

  const {
    messages,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useMessages({
    channelId: conversation?._id,
  });

  const { updateMessage } = useUpdateMessage({
    channelId: conversation?._id,
    goToMessage,
  });

  const channel = useSelector((state) => {
    const { byId } = state.chat.conversations;
    if (conversation?.parentId) {
      return byId?.[conversation?.parentId]?.subchannels.find(
        (s) => s.parentMessageId === conversation?.parentMessageId,
      );
    }
    return byId?.[conversation?._id];
  });

  const handleGotoMessage = (messageId?: string) => {
    const hasMessage = messages.some((m) => m._id === messageId);
    setLoadGoTo(!hasMessage);
    setGoToMessage(messageId);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    markedAsReadRef.current = false;
  }, [conversation._id]);

  useEffect(() => {
    if (markedAsReadRef.current || !messages?.length) return () => {};
    markedAsReadRef.current = true;

    if (!channel?.unreadMessages?.length) return;

    dispatch(
      markConversationAsRead({
        conversationId: conversation._id,
        // TODO: change to send messeges based on intersection observer
        messages: channel.unreadMessages,
      }),
    );
  }, [messages, conversation._id, channel?.unreadMessages]);

  const itemContent = (index: number, message: ChatMessageListItem) => (
    <div key={`divider-${message._id}-${index}`}>
      {!index && !hasNextPage && (
        <ChatInitialState conversation={conversation} messages={messages} />
      )}
      {!message.isSameDayAsPreviousMessage && (
        <ConversationMessageDateDivider date={message.createdAt} />
      )}
      <ChatMessageItem
        hideAuthor={message.isSameAuthorAsPreviousMessage}
        message={message}
        conversation={conversation}
        updateMessage={updateMessage}
        editor={editor}
      />
      {conversation.hasUnreadMessages &&
        conversation.lastReadMessage === message._id &&
        messages.length - 1 !== index && <ConversationNewMessagesDivider />}
    </div>
  );

  if (!isFetching && !messages.length)
    return <ChatInitialState conversation={conversation} />;

  return (
    <>
      {isFetching && <Loading />}
      <ConversationVirtuoso<IChatMessage>
        messages={messages}
        goToMessage={goToMessage}
        setGoToMessage={handleGotoMessage}
        scrollEnd={scrollEnd}
        isFetching={isFetching}
        isFetchingNextPage={isFetchingNextPage}
        isFetchingPreviousPage={isFetchingPreviousPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        fetchNextPage={fetchNextPage as any}
        fetchPreviousPage={fetchPreviousPage as any}
        itemContent={itemContent}
      />
    </>
  );
}
